import {
    addDoc,
    arrayUnion,
    collection,
    doc,
    endBefore,
    getDoc,
    getDocs,
    limit,
    onSnapshot,
    orderBy,
    query,
    runTransaction,
    serverTimestamp,
    setDoc,
    startAfter,
    updateDoc,
    where,
    writeBatch
} from 'firebase/firestore';
import DataOps from './DataOps';

export type ChatType = 'one-to-one' | 'group';

export type Chat = {
    chatId: string;
    type: ChatType;
    participantIds: string[];
    participantTypes?: Array<'family' | 'host' | 'coach'>;
    participantNames?: string[];
    participantAvatars?: string[];
    participantKey?: string;
    name?: string;
    createdAt: any;
    lastMessage?: string;
    lastMessageTimestamp?: any;
    unreadCountByUser?: { [userId: string]: number };
};

export type Message = {
    messageId: string;
    senderId: string;
    text?: string;
    attachments?: Array<{ type: string; url: string; name?: string; size?: number }>;
    createdAt: any;
};

function buildOneToOneKey(a: string, b: string): string {
    const na = String(a || '').trim();
    const nb = String(b || '').trim();
    return [na, nb].sort().join('__');
}

class MessagingService {
    static chatsCollectionPath = 'chats';

    static async getOrCreateOneToOneChat(
        userAId: string,
        userBId: string,
        opts?: {
            participantNames?: string[];
            participantAvatars?: string[];
            participantTypes?: Array<'family' | 'host' | 'coach'>;
        }
    ): Promise<string> {
        if (!userAId || !userBId || userAId === userBId) throw new Error('Invalid participant ids');
        const key = buildOneToOneKey(userAId, userBId);

        const chatsRef = collection(DataOps.db, MessagingService.chatsCollectionPath);
        const q1 = query(chatsRef, where('type', '==', 'one-to-one'), where('participantKey', '==', key), limit(1));
        const snap1 = await getDocs(q1);
        if (!snap1.empty) return snap1.docs[0].id;

        const deterministicId = `one2one__${key}`;
        const chatRef = doc(DataOps.db, MessagingService.chatsCollectionPath, deterministicId);
        await runTransaction(DataOps.db, async (tx) => {
            const existing = await tx.get(chatRef);
            if (existing.exists()) return;
            const initialUnread: { [k: string]: number } = { [userAId]: 0, [userBId]: 0 };
            tx.set(chatRef, {
                type: 'one-to-one',
                participantIds: [String(userAId).trim(), String(userBId).trim()],
                participantKey: key,
                participantTypes: opts?.participantTypes || [],
                participantNames: opts?.participantNames || [],
                participantAvatars: opts?.participantAvatars || [],
                unreadCountByUser: initialUnread,
                createdAt: serverTimestamp(),
                lastMessage: '',
                lastMessageTimestamp: serverTimestamp(),
            } as any);
        });
        return deterministicId;
    }

    static async createGroupChat(
        participantIds: string[],
        opts?: {
            name?: string;
            participantNames?: string[];
            participantAvatars?: string[];
            participantTypes?: Array<'family' | 'host' | 'coach'>;
        }
    ): Promise<string> {
        if (!participantIds || participantIds.length < 2) throw new Error('At least two participants required');
        const initialUnread: { [k: string]: number } = {};
        participantIds.forEach((id) => { initialUnread[id] = 0; });
        const docRef = await addDoc(collection(DataOps.db, MessagingService.chatsCollectionPath), {
            type: 'group',
            name: opts?.name || '',
            participantIds,
            participantTypes: opts?.participantTypes || [],
            participantNames: opts?.participantNames || [],
            participantAvatars: opts?.participantAvatars || [],
            createdAt: serverTimestamp(),
            lastMessage: '',
            lastMessageTimestamp: serverTimestamp(),
            unreadCountByUser: initialUnread,
        });
        return docRef.id;
    }

    static async sendMessage(
        chatId: string,
        senderId: string,
        payload: { text?: string; attachments?: Array<{ type: string; url: string; name?: string; size?: number }> }
    ): Promise<string> {
        if (!chatId || !senderId) throw new Error('Missing chatId/senderId');
        const msg = {
            senderId,
            text: payload.text || '',
            attachments: payload.attachments || [],
            createdAt: serverTimestamp(),
        };
        const messagesCol = collection(DataOps.db, `${MessagingService.chatsCollectionPath}/${chatId}/messages`);
        const msgRef = doc(messagesCol);
        const chatRef = doc(DataOps.db, MessagingService.chatsCollectionPath, chatId);

        const batch = writeBatch(DataOps.db);
        batch.set(msgRef, msg);
        batch.set(chatRef, {
            lastMessage: payload.text || (Array.isArray(payload.attachments) && payload.attachments.length > 0 ? '[Attachment]' : ''),
            lastMessageTimestamp: serverTimestamp(),
        } as any, { merge: true });
        await batch.commit();
        return msgRef.id;
    }

    static listenToUserChats(userId: string, onUpdate: (chats: Chat[]) => void) {
        const colRef = collection(DataOps.db, MessagingService.chatsCollectionPath);
        const qRef = query(colRef, where('participantIds', 'array-contains', userId), orderBy('lastMessageTimestamp', 'desc'));

        try {
            return onSnapshot(qRef, (snap) => {
                const list: Chat[] = snap.docs.map((d) => ({ chatId: d.id, ...(d.data() as any) }));
                onUpdate(list);
            }, () => {
                try {
                    const q2 = query(colRef, where('participantIds', 'array-contains', userId));
                    const unsub2 = onSnapshot(q2, (snap2) => {
                        const list: Chat[] = snap2.docs.map((d) => ({ chatId: d.id, ...(d.data() as any) }));
                        list.sort((a: any, b: any) => {
                            const ta = a?.lastMessageTimestamp?.toDate ? a.lastMessageTimestamp.toDate().getTime() : (a?.lastMessageTimestamp ? new Date(a.lastMessageTimestamp).getTime() : 0);
                            const tb = b?.lastMessageTimestamp?.toDate ? b.lastMessageTimestamp.toDate().getTime() : (b?.lastMessageTimestamp ? new Date(b.lastMessageTimestamp).getTime() : 0);
                            return tb - ta;
                        });
                        onUpdate(list);
                    });
                    return unsub2;
                } catch {
                    onUpdate([]);
                }
                return () => { };
            });
        } catch {
            (async () => {
                try {
                    const q2 = query(colRef, where('participantIds', 'array-contains', userId));
                    const snap = await getDocs(q2);
                    const list: Chat[] = snap.docs.map((d) => ({ chatId: d.id, ...(d.data() as any) }));
                    list.sort((a: any, b: any) => {
                        const ta = a?.lastMessageTimestamp?.toDate ? a.lastMessageTimestamp.toDate().getTime() : (a?.lastMessageTimestamp ? new Date(a.lastMessageTimestamp).getTime() : 0);
                        const tb = b?.lastMessageTimestamp?.toDate ? b.lastMessageTimestamp.toDate().getTime() : (b?.lastMessageTimestamp ? new Date(b.lastMessageTimestamp).getTime() : 0);
                        return tb - ta;
                    });
                    onUpdate(list);
                } catch {
                    onUpdate([]);
                }
            })();
            return () => { };
        }
    }

    static listenToChatMessages(chatId: string, onUpdate: (messages: Message[]) => void, pageSize: number = 100) {
        const colRef = collection(DataOps.db, `${MessagingService.chatsCollectionPath}/${chatId}/messages`);
        const qRef = query(colRef, orderBy('createdAt', 'asc'), limit(pageSize));
        return onSnapshot(qRef, (snap) => {
            const list: Message[] = snap.docs.map((d) => ({ messageId: d.id, ...(d.data() as any) }));
            onUpdate(list);
        });
    }

    static async fetchMessagesPage(
        chatId: string,
        opts?: { pageSize?: number; afterDoc?: any; beforeDoc?: any; direction?: 'asc' | 'desc' }
    ): Promise<{ messages: Message[]; firstDoc?: any; lastDoc?: any; }> {
        const pageSize = Math.max(1, Math.min(200, opts?.pageSize || 50));
        const dir = opts?.direction === 'desc' ? 'desc' : 'asc';
        const colRef = collection(DataOps.db, `${MessagingService.chatsCollectionPath}/${chatId}/messages`);
        let qRef = query(colRef, orderBy('createdAt', dir as any), limit(pageSize));
        if (opts?.afterDoc) qRef = query(qRef, startAfter(opts.afterDoc));
        if (opts?.beforeDoc) qRef = query(qRef, endBefore(opts.beforeDoc));
        const snap = await getDocs(qRef);
        const docs = snap.docs || [];
        return {
            messages: docs.map((d) => ({ messageId: d.id, ...(d.data() as any) })),
            firstDoc: docs[0],
            lastDoc: docs[docs.length - 1],
        };
    }

    static async markChatRead(chatId: string, userId: string): Promise<void> {
        const chatRef = doc(DataOps.db, MessagingService.chatsCollectionPath, chatId);
        const snap = await getDoc(chatRef);
        if (!snap.exists()) return;
        const field = `unreadCountByUser.${userId}`;
        await updateDoc(chatRef, { [field]: 0 } as any);
        await MessagingService.saveLastReadTimestamp(chatId, userId);
    }

    static async saveLastReadTimestamp(chatId: string, userId: string): Promise<void> {
        const ref = doc(DataOps.db, `${MessagingService.chatsCollectionPath}/${chatId}/lastReadBy`, userId);
        await setDoc(ref, { timestamp: serverTimestamp() }, { merge: true });
    }

    static async addParticipants(chatId: string, newParticipantIds: string[]): Promise<void> {
        if (!chatId || !Array.isArray(newParticipantIds) || newParticipantIds.length === 0) return;
        const chatRef = doc(DataOps.db, MessagingService.chatsCollectionPath, chatId);
        const unreadInit: any = {};
        newParticipantIds.forEach((id) => { unreadInit[`unreadCountByUser.${id}`] = 0; });
        await updateDoc(chatRef, { participantIds: arrayUnion(...newParticipantIds), ...unreadInit });
    }
}

export default MessagingService;
